import React, { useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';

import { useAnnounceLaunch } from 'components/CallScribe/LaunchAnnouncer';
import usePopupLaunchStyles from 'components/CallScribe/popupLaunchStyles';

export type TLaunchStage = 'signing-in' | 'connecting' | 'calling' | 'ringing';

type TStepState = 'done' | 'now' | 'next';

export interface ILaunchStep {
  key: string;
  state: TStepState;
  title: string;
  detail: string;
}

export interface IPopupLaunchStepsProps {
  stage: TLaunchStage;
  signedInAs?: string;
  callee: string;
  number: string;
}

const STAGES: TLaunchStage[] = ['signing-in', 'connecting', 'calling'];

export const launchSteps = ({
  stage,
  signedInAs,
  callee,
  number,
}: IPopupLaunchStepsProps): ILaunchStep[] => {
  const at = stage === 'ringing' ? STAGES.length - 1 : STAGES.indexOf(stage);
  const stateOf = (index: number): TStepState => {
    if (index < at) return 'done';
    return index === at ? 'now' : 'next';
  };
  const signIn = stateOf(0);
  const softphone = stateOf(1);
  const call = stateOf(2);

  return [
    {
      key: 'sign-in',
      state: signIn,
      title: signIn === 'done' ? 'Signed in' : 'Signing you in',
      detail: signIn === 'done'
        ? signedInAs || 'Your MSP Process account'
        : 'Checking your MSP Process session',
    },
    {
      key: 'softphone',
      state: softphone,
      title: softphone === 'done' ? 'Softphone connected' : 'Connecting your softphone',
      detail: (() => {
        if (softphone === 'done') return 'Microphone ready';
        if (softphone === 'now') return 'Allow the microphone if your browser asks';
        return 'Microphone and network';
      })(),
    },
    {
      key: 'call',
      state: call,
      title: `Calling ${callee}`,
      detail: (() => {
        if (call === 'now') return stage === 'ringing' ? 'Waiting for them to answer' : 'Placing the call';
        return callee !== number ? number : 'Starts as soon as your softphone is ready';
      })(),
    },
  ];
};

const PopupLaunchSteps: React.FC<IPopupLaunchStepsProps> = props => {
  const classes = usePopupLaunchStyles();
  const announce = useAnnounceLaunch();
  const steps = launchSteps(props);
  const current = steps.find(step => step.state === 'now')?.title ?? '';

  useEffect(() => {
    if (current) announce(current);
  }, [current, announce]);

  return (
    <ol className={classes.steps} aria-label="Starting your call">
      {steps.map(step => (
        <li
          key={step.key}
          className={classes.step}
          aria-current={step.state === 'now' ? 'step' : undefined}
        >
          <span
            className={[
              classes.dot,
              step.state === 'done' ? classes.dotDone : '',
              step.state === 'now' ? classes.dotNow : '',
            ].join(' ')}
            aria-hidden
          >
            {step.state === 'done' ? <CheckIcon fontSize="small" /> : null}
          </span>
          <span className={classes.text}>
            <span className={`${classes.title} ${step.state === 'next' ? classes.titleNext : ''}`}>
              {step.title}
            </span>
            <span className={classes.detail}>{step.detail}</span>
          </span>
        </li>
      ))}
    </ol>
  );
};

export default PopupLaunchSteps;
